import axios from "axios";
import { fullAPIURL } from "../utils/requests";
import { responseStatusInterceptor } from "../utils/axios.utils";
import { Nullable } from "../types/nullable";
import { GymSubtype, GymType } from "src/constants/gymSubtypes";

const authAxios = axios.create({
  baseURL: `${fullAPIURL}/v2/auth`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

authAxios.interceptors.response.use(responseStatusInterceptor);

export type VerifyTurnstileTokenResponse = {
  success: boolean;
};

export type GooglePlaceDetails = {
  name: Nullable<string>;
  address: Nullable<string>;
  phoneNumber: Nullable<string>;
  rating: Nullable<number>;
  website: Nullable<string>;
  hoursOfOperation: Nullable<string[]>;
};

export type AuthSignupBusinessNeeds = {
  value?: string;
}[];

export type AuthSignupAddress = {
  // Country must be in ISO 3166-1 alpha-2 format (e.g. 'US', 'CA', 'GB')
  country: string;
  city?: string;
  state?: string;
  addressLine1?: string;
  addressLine2?: string;
  postalCode?: string;
  timezone?: string;
};

export type AuthSignupClient = {
  company: string;
  averageMonthlyRevenue: string;
  gymType: GymType;
  gymSubtype: GymSubtype;
  url?: string;
  currentSoftware: string;
  address: AuthSignupAddress;
  businessNeeds: AuthSignupBusinessNeeds;
  yearsOpen?: string;
};

export type AuthSignupUser = {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  source?: string;
};

export type AuthSignupUtm = {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
};

export type HubSpotParams = {
  hubspotCompanyId?: string;
  hubspotDealId?: string;
  hubspotContactId?: string;
};

export type PlanParams = {
  id?: "pp-prod-core-free" | "pp-dev-core-free";
  couponId?: string;
  trialEnd?: number;
  trialFromPlan?: boolean;
  cancelAtPeriodEnd: 0;
};

export type AuthSignupMeta = {
  utm?: AuthSignupUtm;
  plan: PlanParams;
  hubspot?: HubSpotParams;
};

export type AuthSignup = {
  client: AuthSignupClient;
  user: AuthSignupUser;
  meta: AuthSignupMeta;
};

export interface SignupResult {
  id: Nullable<number>;
  uuid: Nullable<string>;
  active: Nullable<number>;
  bankConnected: Nullable<number>;
  company: Nullable<string>;
  email: Nullable<string>;
  currencyIso: Nullable<string>;
  gymType: Nullable<string>;
  joinTimestamp: Nullable<number>;
  phone: Nullable<string>;
  addressLine1: Nullable<string>;
  addressLine2: Nullable<string>;
  subdomain: Nullable<string>;
  city: Nullable<string>;
  websiteUrl: Nullable<string>;
  state: Nullable<string>;
  country: Nullable<number>;
  countryIso2: Nullable<string>;
  countryIso3: Nullable<string>;
  countryName: Nullable<string>;
  postalCode: Nullable<string>;
  latitude: Nullable<string>;
  longitude: Nullable<string>;
  timezone: Nullable<string>;
  gmtOffset: Nullable<string>;
  timezoneOffset: Nullable<number>;
  defaultGatewayId: Nullable<number>;
  stripeUserId: Nullable<string>;
  salesTrackingId: Nullable<string>;
  isMassEmailEnabled: Nullable<number>;
  planType: Nullable<string>;
  spaceId: Nullable<string>;
  externalUuid: Nullable<string>;
}

export async function verifyTurnstileToken(turnstileToken: string) {
  const response = await authAxios.post<VerifyTurnstileTokenResponse>("/bot", {
    turnstileToken,
  });
  return response.data;
}

export async function getBusinessDetails(googlePlaceId: string) {
  const response = await authAxios.get<GooglePlaceDetails>(
    "/business-details",
    {
      params: { placeId: googlePlaceId },
    },
  );
  return response.data;
}

export async function validateEmail(email: string) {
  const response = await authAxios.post("/validate-email", { email });
  return response.data;
}

export async function signup(signupData: AuthSignup) {
  const response = await authAxios.post<SignupResult>("/signup", signupData);
  return response.data;
}

export async function confirmAccount(email: string, code: string) {
  const response = await authAxios.post("/confirm-account", { email, code });
  return response.data;
}
